export type FeedbackKind = "bug" | "feedback";
export type FeedbackSeverity = "blocking" | "major" | "minor";

export type BetaFeedbackRequest = {
  reportId: string;
  kind: FeedbackKind;
  title: string;
  message: string;
  steps?: string;
  expected?: string;
  area?: string;
  severity?: FeedbackSeverity;
  contact?: string;
  attachmentLinks?: string[];
};
export type BetaFeedbackResponse = {
  success: true;
  data: {
    reportId: string;
    status: "pending" | "submitted";
    issueUrl?: string;
    issueNumber?: number;
    delivered: boolean;
  };
};
export type FeedbackDraft = Omit<BetaFeedbackRequest, "reportId">;
export type FeedbackAttempt = { request: BetaFeedbackRequest };

export const FEEDBACK_DRAFT_STORAGE_KEY = "genie_beta_feedback_draft";
export const FEEDBACK_ATTEMPT_STORAGE_KEY = "genie_beta_feedback_attempt";

const optional = (value: string) => value.trim() || undefined;
const uuid = (bytes: Uint8Array) => {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export function createReportId(): string {
  if (typeof crypto !== "undefined") {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    if (typeof crypto.getRandomValues === "function") return uuid(crypto.getRandomValues(new Uint8Array(16)));
  }
  throw new Error("Secure random IDs are not available in this browser.");
}

export function parseEvidenceLinks(value: string): { links: string[]; error?: string } {
  const links = value
    .split(/\r?\n/)
    .map(link => link.trim())
    .filter(Boolean);
  if (links.length > 5) return { links, error: "Add at most five HTTPS links." };
  if (
    links.some(link => {
      if (link.length > 2048 || /[?#].*(?:token|key|secret|password)=/i.test(link)) return true;
      try {
        const url = new URL(link);
        return url.protocol !== "https:" || Boolean(url.username || url.password);
      } catch {
        return true;
      }
    })
  )
    return { links, error: "Every evidence link must be a valid HTTPS URL without credentials, tokens, or keys." };
  return { links };
}

export function toFeedbackRequest(
  draft: FeedbackDraft,
  reportId: string
): { request?: BetaFeedbackRequest; error?: string } {
  const title = draft.title.trim();
  const message = draft.message.trim();
  if (title.length < 3 || title.length > 160) return { error: "Use a title between 3 and 160 characters." };
  if (message.length < 3) return { error: "Describe what happened in at least three characters." };
  if (message.length > 5000) return { error: "Keep the report under 5,000 characters." };
  if (
    (draft.steps?.trim().length ?? 0) > 5000 ||
    (draft.expected?.trim().length ?? 0) > 2000 ||
    (draft.area?.trim().length ?? 0) > 80
  )
    return { error: "One of the optional fields is too long." };
  if (draft.contact && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contact.trim()) || draft.contact.trim().length > 320))
    return { error: "Enter a valid email address, or leave it blank." };
  const evidence = parseEvidenceLinks(draft.attachmentLinks?.join("\n") ?? "");
  if (evidence.error) return { error: evidence.error };
  return {
    request: {
      reportId,
      kind: draft.kind,
      title,
      message,
      steps: optional(draft.steps ?? ""),
      expected: optional(draft.expected ?? ""),
      area: optional(draft.area ?? ""),
      severity: draft.severity,
      contact: optional(draft.contact ?? ""),
      attachmentLinks: evidence.links.length ? evidence.links : undefined,
    },
  };
}

export function isSafeIssueUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      url.hostname === "github.com" &&
      /^\/[^/]+\/[^/]+\/issues\/\d+$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

export function parseFeedbackResponse(input: unknown, reportId: string): BetaFeedbackResponse | null {
  if (!input || typeof input !== "object") return null;
  const data = (input as { data?: unknown }).data;
  if ((input as { success?: unknown }).success !== true || !data || typeof data !== "object") return null;
  const value = data as Record<string, unknown>;
  if (
    value.reportId !== reportId ||
    (value.status !== "pending" && value.status !== "submitted") ||
    typeof value.delivered !== "boolean"
  )
    return null;
  if ((value.status === "submitted") !== value.delivered) return null;
  if (value.issueUrl !== undefined && (typeof value.issueUrl !== "string" || !isSafeIssueUrl(value.issueUrl)))
    return null;
  if (
    value.issueNumber !== undefined &&
    (typeof value.issueNumber !== "number" || !Number.isInteger(value.issueNumber) || value.issueNumber <= 0)
  )
    return null;
  return {
    success: true,
    data: {
      reportId,
      status: value.status,
      delivered: value.delivered,
      ...(typeof value.issueUrl === "string" ? { issueUrl: value.issueUrl } : {}),
      ...(typeof value.issueNumber === "number" ? { issueNumber: value.issueNumber } : {}),
    },
  };
}
